import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { DatePicker, Button, Table, Divider, Tag, message } from 'antd';
import { apiForteSettlementSummary, apiForteSettlementTransactions } from 'services/dashboardService';

const { RangePicker } = DatePicker;

const formatCurrency = (val) => {
    if (val == null || val === '') return '';
    const num = parseFloat(val);
    if (isNaN(num)) return val;
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(num);
};

const SUMMARY_COLUMNS = [
    { title: 'Settle Date',  dataIndex: 'forte_settle_date',     key: 'forte_settle_date',     width: 120 },
    { title: 'Funding ID',   dataIndex: 'forte_funding_id',      key: 'forte_funding_id',      width: 200, ellipsis: true },
    { title: 'Batch ID',     dataIndex: 'forte_settle_batch_id', key: 'forte_settle_batch_id', width: 130 },
    { title: 'Type',         dataIndex: 'forte_settle_type',     key: 'forte_settle_type',     width: 90 },
    { title: 'Transactions', dataIndex: 'transaction_count',     key: 'transaction_count',     width: 120, align: 'right' },
    { title: 'Gross',        dataIndex: 'gross_amount',          key: 'gross_amount',          width: 130, align: 'right', render: formatCurrency },
    { title: 'Fee',          dataIndex: 'fee_amount',            key: 'fee_amount',            width: 120, align: 'right', render: formatCurrency },
    { title: 'Net',          dataIndex: 'net_amount',            key: 'net_amount',            width: 120, align: 'right', render: formatCurrency },
];

const DETAIL_COLUMNS = [
    { title: 'Txn Date',       dataIndex: 'transaction_date',    key: 'transaction_date',    width: 110 },
    { title: 'Transaction ID', dataIndex: 'transaction_id',      key: 'transaction_id',      width: 170, ellipsis: true },
    { title: 'Payer',          dataIndex: 'payer_name',          key: 'payer_name',          width: 160 },
    { title: 'Email',          dataIndex: 'p_email',             key: 'p_email',             width: 200, ellipsis: true },
    { title: 'Registration',   dataIndex: 'registration_name',   key: 'registration_name',   ellipsis: true },
    { title: 'Charged',        dataIndex: 'total_checkout',      key: 'total_checkout',      width: 110, align: 'right', render: formatCurrency },
    { title: 'Fee',            dataIndex: 'processing_fee',      key: 'processing_fee',      width: 110, align: 'right', render: formatCurrency },
    { title: 'Settled',        dataIndex: 'forte_settle_amount', key: 'forte_settle_amount', width: 120, align: 'right', render: formatCurrency },
    { title: 'Type',           dataIndex: 'forte_settle_type',   key: 'forte_settle_type',   width: 90 },
];

const PARTICIPANT_COLUMNS = [
    { title: 'Name',     key: 'name',          width: 180,
      render: (_, p) => `${p.camper_first_name || ''} ${p.camper_last_name || ''}`.trim() || '—' },
    { title: 'Email',    dataIndex: 'camper_email', key: 'camper_email', width: 200, ellipsis: true },
    { title: 'Programs', key: 'programs',
      render: (_, p) => {
          if (!p.programs || p.programs.length === 0) return <span style={{ color: '#999' }}>—</span>;
          return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {p.programs.map((prog, i) => (
                      <Tag key={i} color="blue" style={{ marginBottom: 2 }}>
                          {prog.program_name}
                          {prog.price_at_purchase > 0 ? ` (${formatCurrency(prog.price_at_purchase)})` : ''}
                      </Tag>
                  ))}
              </div>
          );
      }
    },
];

// Expanded row content for each transaction — shows participants + their programs
const ExpandedRow = ({ record }) => {
    const participants = record.participants || [];
    if (participants.length === 0) {
        return <div style={{ padding: '8px 16px', color: '#999', fontStyle: 'italic' }}>No participant records found.</div>;
    }
    return (
        <div style={{ padding: '8px 16px' }}>
            <Table
                dataSource={participants}
                columns={PARTICIPANT_COLUMNS}
                rowKey={(p, i) => `${p.camper_first_name}-${p.camper_last_name}-${i}`}
                pagination={false}
                size="small"
                bordered={false}
                style={{ background: '#fafafa' }}
                rowClassName={(_, idx) => idx % 2 === 1 ? 'table-row-light' : ''}
            />
        </div>
    );
};

function ForteSettlementReport() {
    const userDataStore = useSelector((x) => x.userDataStore);
    const appDataStore  = useSelector((x) => x.appDataStore);

    const [dateRange, setDateRange]             = useState(null);
    const [isBusy, setIsBusy]                   = useState(false);
    const [summaryData, setSummaryData]         = useState(null);
    const [selectedFunding, setSelectedFunding] = useState(null);
    const [detailRows, setDetailRows]           = useState(null);
    const [detailLoading, setDetailLoading]     = useState(false);

    const clubId = appDataStore?.club?.id;

    const handleRun = () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            message.warning('Please select a date range.');
            return;
        }

        const date_from = dateRange[0].format('YYYY-MM-DD');
        const date_to   = dateRange[1].format('YYYY-MM-DD');

        setIsBusy(true);
        setSummaryData(null);
        setSelectedFunding(null);
        setDetailRows(null);

        apiForteSettlementSummary({
            token:    userDataStore.token,
            club_id:  clubId,
            date_from,
            date_to,
        })
            .then((res) => {
                if (res) {
                    setSummaryData(res);
                    if (res.settlements?.length === 0) {
                        message.info('No settlement data found for that date range.');
                    }
                } else {
                    message.info('No settlement data found.');
                }
                setIsBusy(false);
            })
            .catch(() => {
                message.error('Failed to load settlement data.');
                setIsBusy(false);
            });
    };

    const handleRowClick = (record) => {
        const fundingId = record.forte_funding_id;
        if (selectedFunding === fundingId) {
            setSelectedFunding(null);
            setDetailRows(null);
            return;
        }
        setSelectedFunding(fundingId);
        setDetailRows(null);
        setDetailLoading(true);

        apiForteSettlementTransactions({
            token:      userDataStore.token,
            club_id:    clubId,
            funding_id: fundingId,
        })
            .then((res) => {
                setDetailRows(res?.transactions || []);
                setDetailLoading(false);
            })
            .catch(() => {
                message.error('Failed to load transaction detail.');
                setDetailLoading(false);
            });
    };

    const totals = summaryData?.totals;

    return (
        <div>
            <h2 className="report-title">Forte Settlement Report</h2>

            <div style={{ marginTop: 24, marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <RangePicker
                    value={dateRange}
                    onChange={setDateRange}
                    format="YYYY-MM-DD"
                />
                <Button type="primary" onClick={handleRun} loading={isBusy}>
                    Run Report
                </Button>
            </div>

            {summaryData && (
                <>
                    {totals && (
                        <div style={{
                            padding: '12px 16px',
                            backgroundColor: '#f0f5ff',
                            borderRadius: 8,
                            border: '1px solid #d6e4ff',
                            marginBottom: 16,
                            display: 'flex',
                            gap: 32,
                            flexWrap: 'wrap',
                        }}>
                            <div><strong>Period:</strong> {summaryData.date_from} to {summaryData.date_to}</div>
                            <div><strong>Batches:</strong> {summaryData.settlements.length}</div>
                            <div><strong>Transactions:</strong> {totals.transaction_count}</div>
                            <div><strong>Gross:</strong> {formatCurrency(totals.gross_amount)}</div>
                            <div><strong>Fees:</strong> {formatCurrency(totals.fee_amount)}</div>
                            <div><strong>Net:</strong> {formatCurrency(totals.net_amount)}</div>
                        </div>
                    )}

                    <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #eee', padding: 12, overflowX: 'auto' }}>
                        <Table
                            dataSource={summaryData.settlements}
                            columns={SUMMARY_COLUMNS}
                            rowKey={(r) => r.forte_funding_id || r.forte_settle_batch_id || r.forte_settle_date}
                            pagination={false}
                            bordered
                            size="middle"
                            rowClassName={(record) =>
                                selectedFunding === record.forte_funding_id ? 'table-row-total' : ''
                            }
                            onRow={(record) => ({
                                onClick: () => handleRowClick(record),
                                style: { cursor: 'pointer' },
                            })}
                            scroll={{ x: 'max-content' }}
                        />
                    </div>

                    {selectedFunding && (
                        <div style={{ marginTop: 24 }}>
                            <Divider orientation="left" style={{ fontWeight: 600 }}>
                                Transactions — Funding ID: {selectedFunding}
                            </Divider>
                            <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #eee', padding: 12, overflowX: 'auto' }}>
                                <Table
                                    dataSource={detailRows || []}
                                    columns={DETAIL_COLUMNS}
                                    rowKey={(r, i) => r.transaction_id || i}
                                    loading={detailLoading}
                                    pagination={{ pageSize: 50 }}
                                    bordered
                                    size="small"
                                    rowClassName={(_, idx) => idx % 2 === 1 ? 'table-row-light' : ''}
                                    scroll={{ x: 'max-content' }}
                                    expandable={{
                                        expandedRowRender: (record) => <ExpandedRow record={record} />,
                                        rowExpandable: (record) => (record.participants?.length ?? 0) > 0,
                                        expandRowByClick: false,
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}

            {!summaryData && !isBusy && (
                <div style={{
                    marginTop: 24,
                    padding: '40px',
                    textAlign: 'center',
                    backgroundColor: '#fff',
                    borderRadius: 8,
                    border: '1px solid #eee',
                    color: '#999',
                    fontSize: 16,
                }}>
                    Select a date range and click Run Report.
                </div>
            )}
        </div>
    );
}

export default ForteSettlementReport;
